import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { API_BASE } from '../config/api';
import { formatApiErrorDetail } from '../lib/apiError';

export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setError('This link is missing its token. Use the link from your invite email.'); return; }
    axios.get(`${API_BASE}/api/auth/set-password/${encodeURIComponent(token)}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail, 'This link is invalid or has expired.')));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error('Password must be at least 8 characters');
    if (password !== confirm) return toast.error('Passwords do not match');
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/api/auth/set-password`, { token, password });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const field = 'pl-10 bg-navigate-surface border-zinc-800 text-white placeholder:text-zinc-500';
  return (
    <div className="min-h-screen bg-navigate-dark flex items-center justify-center px-4" data-testid="set-password-page">
      <div className="w-full max-w-md bg-zinc-900/70 border border-zinc-800 rounded-2xl p-8">
        <h1 className="font-heading text-2xl font-bold text-white mb-1">Set your password</h1>
        {info && <p className="text-sm text-zinc-400 mb-6" data-testid="set-password-email">for {info.email}</p>}
        {error && (
          <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex gap-3 text-red-300 text-sm" data-testid="set-password-error">
            <AlertCircle className="h-5 w-5 shrink-0" />{error}
          </div>
        )}
        {!error && !info && <Loader2 className="h-6 w-6 animate-spin text-zinc-500 mt-6" />}
        {done ? (
          <div className="mt-2 space-y-5" data-testid="set-password-success">
            <p className="flex items-center gap-2 text-teal-300"><CheckCircle2 className="h-5 w-5" />Your password is set.</p>
            <Link to={`/login?email=${encodeURIComponent(info?.email || '')}`} className="block w-full btn-gold text-center" data-testid="set-password-signin-link">Sign in</Link>
          </div>
        ) : info && (
          <form onSubmit={submit} className="space-y-5">
            {[['New password', password, setPassword, 'set-password-input'], ['Confirm password', confirm, setConfirm, 'set-password-confirm-input']].map(([label, val, set, tid]) => (
              <div key={tid} className="space-y-2">
                <Label className="text-zinc-300">{label}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                  <Input type="password" autoComplete="new-password" required value={val} onChange={(e) => set(e.target.value)} className={field} data-testid={tid} />
                </div>
              </div>
            ))}
            <p className="text-xs text-zinc-500">At least 8 characters.</p>
            <button type="submit" disabled={saving} className="w-full btn-gold disabled:opacity-50" data-testid="set-password-submit">
              {saving ? 'Saving…' : 'Set password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
